/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { std } from '@funkey/shared/utils';
import { Toggle } from '~/components/primitives';
import {
    ActivityFilterBar_Mobile_SelectFilter,
    displayFirstN,
    useBrowseCategories,
    type ActivityFilterCategory,
    type ActivityFilterConfig,
} from './shared';
import { usePortalCtx } from '~/features/global/portal';
import { useI18n } from '~/features/i18n';
import { useMemo } from 'react';
import { useActivitySearchCtx } from '../../search/client/context';

export const MULTIPLE_CATEGORIES_FILTER_CONFIG: ActivityFilterConfig<
    {
        /** Category IDs */
        categories: number[];
        subCategories: number[];
    },
    ReturnType<typeof useBrowseCategories>
> = {
    key: 'categories',
    label: i18n => i18n._('Categories'),
    placeholder: i18n => i18n._('All categories'),
    selectionDisplay(i18n, value, data, renderLocation) {
        const totalSelected = value.categories.length + value.subCategories.length;

        if (totalSelected === 0) return undefined;
        if (totalSelected === 1) {
            const selectedId = value.categories[0] ?? value.subCategories[0];
            const category = data.categories?.find(category => category.id === selectedId);
            return category?.filterName ?? category?.name;
        }
        if (totalSelected === data.categories?.length) return i18n._('All categories');
        if (data.categories === undefined || renderLocation === 'desktop')
            return `${totalSelected} ${i18n._('categories')}`;

        return displayFirstN(
            [...value.categories, ...value.subCategories].map(id => {
                const category = data.categories!.find(category => category.id === id);
                return category?.filterName ?? category?.name;
            }),
            3,
        );
    },

    useDataLoader: (_filterState: Record<string, unknown>) => {
        const portal = usePortalCtx();
        // searchCtx may be null if not inside ActivitySearchCtxProvider (e.g., selection page)
        const searchCtx = useActivitySearchCtx();
        return useBrowseCategories({
            tenantId: portal.portal === 'BIZZ' ? portal.bizz.tenantId : undefined,
            // WEB category-landing pages narrow root options via the search
            // customization; BIZZ browse narrowing comes from the rules atom.
            allowedCategories: searchCtx?.customization?.constraints?.allowedCategories,
        });
    },

    PopupContent(props) {
        const portal = usePortalCtx();
        const i18n = useI18n();
        const isBizzContext = portal.portal === 'BIZZ';

        // Group categories by type
        // Global categories: only show root categories (no parent) that have no tenants
        const globalCategories =
            props.data.categories
                ?.filter(category => category.isRoot && !category.isCustom)
                .sort((a, b) => a.name.localeCompare(b.name)) || [];

        // Custom categories: show ALL tenant-specific categories (including subcategories)
        const customCategories =
            props.data.categories
                ?.filter(category => category.isCustom)
                .sort((a, b) => a.name.localeCompare(b.name)) || [];

        const renderCategoryToggle = (category: ActivityFilterCategory) => {
            const catId = category.id;
            const isSubcategory = !category.isRoot;
            // Check if this category is selected (in either categories or subCategories)
            const isSelected = isSubcategory
                ? props.value.subCategories.includes(catId)
                : props.value.categories.includes(catId);

            return (
                <Toggle
                    key={category.id}
                    pressed={isSelected}
                    onPressedChange={pressed => {
                        props.onChange(prev => {
                            if (isSubcategory) {
                                // Handle subcategory selection
                                return {
                                    ...prev,
                                    subCategories: pressed
                                        ? [...prev.subCategories.filter(id => id !== catId), catId]
                                        : prev.subCategories.filter(id => id !== catId),
                                };
                            }
                            // Handle root category selection
                            return {
                                ...prev,
                                categories: pressed
                                    ? [...prev.categories.filter(id => id !== catId), catId]
                                    : prev.categories.filter(id => id !== catId),
                                subCategories: [],
                            };
                        });
                    }}
                >
                    {category.filterName ?? category.name}
                </Toggle>
            );
        };

        return (
            <div className="max-w-[375px] overflow-y-auto lg:max-h-[min(400px,80vh)]">
                {/* Custom Categories (only show if in BIZZ context and there are custom categories) */}
                {isBizzContext && customCategories.length > 0 && (
                    <div className="mb-4">
                        <h4 className="mb-2 text-sm font-medium text-secondary">
                            {i18n._('filters.categories.custom')}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            {customCategories.map(renderCategoryToggle)}
                        </div>
                    </div>
                )}

                {/* Global Categories */}
                {globalCategories.length > 0 && (
                    <div className="mb-4">
                        {isBizzContext && customCategories.length > 0 && (
                            <h4 className="mb-2 text-sm font-medium text-secondary">
                                {i18n._('filters.categories.general')}
                            </h4>
                        )}
                        <div className="flex flex-wrap gap-2">
                            {globalCategories.map(renderCategoryToggle)}
                        </div>
                    </div>
                )}

                {/* Fallback: show all categories if no grouping applies */}
                {!isBizzContext && customCategories.length > 0 && globalCategories.length === 0 && (
                    <div className="flex flex-wrap gap-2">
                        {customCategories.map(renderCategoryToggle)}
                    </div>
                )}
            </div>
        );
    },

    mobile: {
        icon: 'activity-selection',
        ctaText: i18n => i18n._('Choose categories'),
        Component(props) {
            function onChange(categoryID: number | null) {
                return props.onChange(prev => ({
                    ...prev,
                    categories: categoryID === null ? [] : [categoryID],
                    subCategories: [],
                }));
            }

            return (
                <ActivityFilterBar_Mobile_SelectFilter
                    value={props.value.categories[0]?.toString() ?? null}
                    onChange={value => {
                        if (value === null) {
                            onChange(null);
                        } else {
                            const id = std.Number.safeParseInt(value);
                            if (id === null) return;
                            onChange(id);
                        }
                    }}
                    placeholder={props.i18n._('filters.select.category.defaultvalue')}
                    options={useMemo(() => {
                        if (!props.data.categories?.length) return [];

                        return props.data.categories
                            .filter(x => x.isRoot)
                            .map(x => ({
                                value: String(x.id),
                                label: x.filterName || x.name,
                            }))
                            .sort((a, b) => a.label.localeCompare(b.label, props.i18n.locale));
                    }, [props.data.categories])}
                />
            );
        },
    },
};
